/**
 * Theme preference: light, dark, or follow the operating system.
 *
 * The chosen theme is applied by toggling a `dark` class on <html>, which is
 * what Tailwind's class-based dark mode keys off. The initial application
 * happens in `public/theme-init.js` so the correct theme is painted on the
 * first frame — doing it here would flash the light theme first.
 *
 * The state lives in this module rather than in `useState` inside the hook.
 * The theme is one property of one document, but `useTheme()` is called from
 * more than one place — the toggle in the header and the dashboard, which
 * needs the resolved value to re-read the chart colours. Per-hook state gave
 * each caller its own private copy: pressing the toggle updated the toggle's
 * copy and the DOM, and the dashboard's copy went stale, so the charts kept
 * painting the previous theme's colours until something else happened to
 * re-render them. A single store with `useSyncExternalStore` means every
 * caller sees the same value and re-renders together.
 */
import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'titrate-theme';

export function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing or blocked storage — fall back to following the system.
  }
  return 'system';
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  return preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference;
}

/**
 * Browser-chrome colour per theme. These are the `--surface` token values, so
 * the address bar / status bar reads as a continuation of the sticky header
 * rather than a band of the wrong brightness above it. Kept in sync with
 * index.css by hand — there are only two, and reading them back out of
 * `getComputedStyle` before the class change has applied is worse.
 */
const THEME_COLOR: Record<'light' | 'dark', string> = {
  light: '#ffffff',
  dark: '#111a2b',
};

// ---------------------------------------------------------------------------
// The store.
// ---------------------------------------------------------------------------

let preference: ThemePreference = readPreference();
let resolved: 'light' | 'dark' = resolveTheme(preference);

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * `useSyncExternalStore` calls this on every render and bails out of a
 * re-render when the result is unchanged, so it has to return a cached value
 * rather than compute a fresh one — both of these are plain module variables
 * for exactly that reason.
 */
function getPreference(): ThemePreference {
  return preference;
}

function getResolved(): 'light' | 'dark' {
  return resolved;
}

function applyToDocument(next: 'light' | 'dark') {
  document.documentElement.classList.toggle('dark', next === 'dark');
  document.getElementById('theme-color')?.setAttribute('content', THEME_COLOR[next]);
}

/**
 * The DOM is updated before subscribers are notified, so that by the time a
 * component re-renders the new custom-property values are already live. The
 * dashboard depends on this: it reads the chart palette out of
 * `getComputedStyle` during its render.
 */
function commit() {
  applyToDocument(resolved);
  for (const listener of listeners) listener();
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // A preference we cannot persist still applies for this visit.
  }
  resolved = resolveTheme(next);
  commit();
}

// Tracking the OS preference is a property of the page, not of any component
// that happens to be mounted, so it is subscribed once here. While the
// preference is anything other than `system` this resolves to no change and
// notifies nobody.
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const next = resolveTheme(preference);
    if (next === resolved) return;
    resolved = next;
    commit();
  });
}

export function useTheme() {
  return {
    preference: useSyncExternalStore(subscribe, getPreference),
    resolved: useSyncExternalStore(subscribe, getResolved),
    setPreference: setThemePreference,
  };
}

/** Reads a themed chart colour from CSS, so plots follow the active theme. */
export function chartColor(name: 'grid' | 'axis' | 'primary' | 'baseline'): string {
  if (typeof window === 'undefined') return '#94a3b8';
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--chart-${name}`)
    .trim();
  return value ? `rgb(${value})` : '#94a3b8';
}
